<template>
	<main>
		<div v-if="benefitDetail?.data" class="container c_inner">
			<section class="section_header">
				<div class="header_container">
					<p class="header_title">{{ benefitDetail?.data.post[0].post_title }}</p>
					<div class="header_sub">
						<div class="sub_info">
							<span class="user">{{ benefitDetail?.data.post[0].authority_group_name }}</span
							><span class="view">{{ `조회수${benefitDetail?.data.post[0].post_hit}` }}</span>
							<span class="date">{{
								dayjs(benefitDetail?.data.post[0].post_enrollment_date).format('YYYY-MM-DD')
							}}</span>
						</div>
					</div>
				</div>
			</section>

			<section class="section_body">
				<div class="body_box">
					<div class="box_content c_editorContent">
						<div v-dompurify-html="benefitDetail?.data.post[0].post_content" class="c_editorContent"></div>
					</div>
				</div>
			</section>

			<Comments :comments="commentData" :counts="commentCntData" :board="'master'" />
		</div>
		<div v-else class="c_empty"></div>
	</main>
</template>

<script setup lang="ts">
// core
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';

import { useGetPostBenefitDetailApi } from '@/api/benefit';
import { CommentWithReplies } from '@/api/comment';
import Comments from '@/components/postDetail/Comments.vue';
import { COMPANY_TITLE, NO_PERMISSION } from '@/constants/title';
import { usePopupStore } from '@/stores/popup';
import { handleLinkClick } from '@/utils/directive/editorLinkOpenNewTab';

// -----variable-----
const route = useRoute();
const router = useRouter();

const commentData = ref<CommentWithReplies[]>([]);
const commentCntData = ref(0);
const contentBody = ref<HTMLDivElement | null>(null);
const popupStore = usePopupStore();

// -----api-----
const { data: benefitDetail } = useGetPostBenefitDetailApi({
	post_pk: Number(route.params.pk ?? ''),
});

// editor 링크 클릭 시 새창 열기
// COMMENT : AOS에서는 target: _blank로 새창을 띄울 수 없어 추가된 코드입니다.
watch(
	[benefitDetail],
	() => {
		if (benefitDetail.value?.error?.code === '58') {
			popupStore.openPopup({
				content: COMPANY_TITLE,
				subtext: NO_PERMISSION,
				cancel: () => router.back(),
			});
		} else if (!benefitDetail.value?.error) {
			contentBody.value = document.querySelector('.c_editorContent');

			if (contentBody.value) {
				// 기존 리스너 제거
				contentBody.value.removeEventListener('click', handleLinkClick);

				// 새로운 리스너 추가
				contentBody.value.addEventListener('click', handleLinkClick);
			}
		} else {
			popupStore.openPopup({
				content: COMPANY_TITLE,
				subtext: NO_PERMISSION,
				cancel: () => router.back(),
			});
		}
	},
	{ immediate: true }
);
</script>

<style scoped lang="scss">
.container {
	display: flex;
	flex-direction: column;
	// gap: 8rem;
	max-width: 68rem;
	padding-top: var(--header-height);
	margin: 7.2rem auto 8.8rem;
	@media screen and (max-width: 1280px) {
		// padding-bottom: var(--bottomMenu-height);
		margin: 4rem auto 8rem;
	}
}

.section_header {
	.header_container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		padding-bottom: 1.6rem;
		border-bottom: 1px solid #393d45;
		margin-bottom: 4rem;
		.header_title {
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.387rem;
		}
		.header_sub {
			display: flex;
			justify-content: space-between;
			font-size: var(--font-s-small);
			font-weight: var(--font-w-light);
			line-height: 1.551rem;
			color: #818393;
			.sub_info {
				display: flex;
				align-items: center;
				span {
					margin: 0 8px;
					position: relative;
				}
				span:first-child {
					margin-left: 0;
				}
				span:last-child::after {
					display: none; /* 마지막 span에는 border를 추가하지 않음 */
				}
				span::after {
					content: '';
					position: absolute;
					right: -8px;
					top: 50%;
					transform: translateY(-50%);
					height: 8px; /* border의 높이 */
					border-left: 1px solid #393d45; /* 원하는 border 색상으로 변경 */
				}
			}
		}
	}
}

.section_body {
	display: flex;
	flex-direction: column;
	gap: 2.4rem;
	margin-bottom: 8.8rem;
	.body_box {
		background-color: var(--color-bg-mid);
		border-radius: 10px;
		.box_content {
			padding: 2rem;
		}
	}
	.body_btn {
		display: flex;
		justify-content: end;
		gap: 0.8rem;
		button {
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-semi-bold);
			line-height: 1.79rem;
			padding: 1.3rem 1.6rem;
			border-radius: 0.6rem;
		}
		.btn_delete {
			border: 1px solid #818393;
		}
		.btn_modify {
			background-color: #ff5e00;
		}
	}
}

.section_comment {
	.comment_header {
		margin-bottom: 1.6rem;
		.header_title {
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.387rem;
		}
	}
	.comment_inputArea {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		margin-bottom: 3.2rem;
		.inputArea_textArea {
			height: 16.4rem;
		}
		.inputArea_btnArea {
			width: 100%;
			display: flex;
			justify-content: end;
			button {
				font-size: var(--font-s-mid);
				font-weight: var(--font-w-semi-bold);
				line-height: 1.79rem;
				background-color: #ff5e00;
				padding: 1.3rem 1.6rem;
				border-radius: 0.6rem;
			}
		}
	}
}
</style>
